import { signal, effect } from '@preact/signals';
import { tokenizedSseUrl, dashboardToken, apiGet, chatId } from './api';

// Global chat SSE state — opened once when the app mounts so the unread
// badge keeps tracking even when /chat isn't the active page.

export const chatUnread = signal(0);
export const chatStreamConnected = signal(false);

// In-memory broadcast for any page that wants to react to chat events
// (the Chat page subscribes; the sidebar just reads chatUnread).
type Listener = (eventName: string, data: any) => void;
const listeners = new Set<Listener>();

export function subscribeChatStream(fn: Listener): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function resetUnread() { chatUnread.value = 0; }

let started = false;
export function startChatStream() {
  if (started || !dashboardToken) return;
  started = true;

  let es: EventSource | null = null;
  let activeRoute = window.location.pathname;

  // ── Polling fallback state ──────────────────────────────────────────
  // When SSE fails (Cloudflare tunnel buffering, proxy issues), we poll
  // /api/chat/history and /api/chat/status to keep the UI alive.
  let pollTimer: ReturnType<typeof setInterval> | null = null;
  let sseFailCount = 0;
  let lastSeenTs = 0;                   // tracks latest turn timestamp
  let lastSeenCount = 0;                // tracks turn count to detect new ones
  let pollProcessing = false;           // last known processing state from poll
  let sseReceivedData = false;          // true once any SSE event body arrives
  const POLL_INTERVAL = 2000;
  const SSE_FAIL_THRESHOLD = 2;         // start polling after N consecutive SSE errors

  function startPolling() {
    if (pollTimer) return;
    // Seed with current time so we only catch genuinely new turns
    if (lastSeenTs === 0) lastSeenTs = Math.floor(Date.now() / 1000);
    pollTimer = setInterval(pollOnce, POLL_INTERVAL);
    // Run once immediately
    pollOnce();
  }

  function stopPolling() {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
    sseFailCount = 0;
  }

  async function pollOnce() {
    try {
      // Fetch latest turns
      const historyUrl = `/api/chat/history?chatId=${encodeURIComponent(chatId)}&limit=10`;
      const historyData = await apiGet<{ turns: Array<{ role: string; content: string; source?: string; created_at?: number; photoUrl?: string; photoCaption?: string }> }>(historyUrl);
      const turns = historyData.turns || [];

      // Detect new turns by comparing timestamps
      if (turns.length > 0) {
        // turns come newest-first from the API, so find ones newer than lastSeenTs
        const newTurns = turns.filter(t => (t.created_at || 0) > lastSeenTs);
        // Dispatch in chronological order (oldest first)
        newTurns.reverse();
        for (const t of newTurns) {
          const eventName = t.role === 'user' ? 'user_message' : 'assistant_message';
          dispatch(eventName, t);
          if ((t.created_at || 0) > lastSeenTs) {
            lastSeenTs = t.created_at || 0;
          }
        }
        lastSeenCount = turns.length;
      }

      // Fetch processing status
      const statusUrl = `/api/chat/status?chatId=${encodeURIComponent(chatId)}`;
      const statusData = await apiGet<{ processing: boolean; latestTs: number }>(statusUrl);
      if (statusData.processing !== pollProcessing) {
        pollProcessing = statusData.processing;
        dispatch('processing', { processing: pollProcessing, chatId });
      }

      // Mark as "connected" since polling is working
      chatStreamConnected.value = true;
    } catch {
      // Poll failed, keep trying
    }
  }

  function dispatch(eventName: string, data: any) {
    if (eventName === 'assistant_message' && !activeRoute.startsWith('/chat')) {
      chatUnread.value = chatUnread.value + 1;
    }
    for (const l of listeners) {
      try { l(eventName, data); } catch (err) { console.error('chat listener', err); }
    }
  }

  // ── Route tracking ──────────────────────────────────────────────────
  function reactToRoute() { activeRoute = window.location.pathname; }
  window.addEventListener('popstate', reactToRoute);
  // Wouter pushes via history.pushState; patch to fire popstate.
  const origPush = history.pushState;
  history.pushState = function (...args: any[]) {
    const ret = origPush.apply(this as any, args as any);
    reactToRoute();
    return ret;
  };

  // ── SSE connection ──────────────────────────────────────────────────
  function open() {
    if (es) return;
    es = new EventSource(tokenizedSseUrl('/api/chat/stream'));

    es.onopen = () => {
      chatStreamConnected.value = true;
      sseFailCount = 0;
      stopPolling();
    };

    es.onerror = () => {
      chatStreamConnected.value = false;
      sseFailCount++;
      // If SSE keeps failing, switch to polling. This handles Cloudflare
      // tunnel buffering where the connection "opens" but never delivers data,
      // as well as hard failures.
      if (sseFailCount >= SSE_FAIL_THRESHOLD) {
        startPolling();
      }
    };

    const sseDispatch = (eventName: string) => (ev: MessageEvent) => {
      let data: any;
      try { data = JSON.parse(ev.data); } catch { return; }
      sseReceivedData = true;
      // Track the latest timestamp from SSE events so polling (if it
      // takes over later) doesn't replay messages we already have.
      if (data.created_at && data.created_at > lastSeenTs) {
        lastSeenTs = data.created_at;
      }
      dispatch(eventName, data);
    };

    es.addEventListener('user_message', sseDispatch('user_message'));
    es.addEventListener('assistant_message', sseDispatch('assistant_message'));
    es.addEventListener('assistant_photo', sseDispatch('assistant_photo'));
    es.addEventListener('processing', sseDispatch('processing'));
    es.addEventListener('progress', sseDispatch('progress'));
    es.addEventListener('error', sseDispatch('error') as any);
  }

  open();

  // If SSE never delivers any data within 5 seconds, assume it's being
  // buffered and kick off polling proactively. This catches the case where
  // EventSource reports onopen (HTTP 200 received) but the body is buffered
  // by Cloudflare or another reverse proxy.
  setTimeout(() => {
    if (!sseReceivedData) {
      startPolling();
    }
  }, 5000);
}

// When the user navigates onto /chat, clear the unread count.
effect(() => {
  if (typeof window === 'undefined') return;
  const onChat = () => {
    if (window.location.pathname.startsWith('/chat')) {
      resetUnread();
    }
  };
  window.addEventListener('popstate', onChat);
  // Initial check.
  onChat();
});
